import {
  ClientWorkFlowStatus,
  FrequencyAndScheduleStatus,
  IClientInvoice,
  IClientInvoiceCreateRequest,
  IClientInvoiceTemplate,
  IClientInvoiceTemplateUpdateRequest,
  IClientInvoiceUpdateRequest,
  ICollaboratorSchema,
  IDeductionResponse,
  IInvoice,
  IInvoiceCollaboratorCreateRequest,
  IInvoiceTemplate,
  InvoiceStatus,
  Intervals,
  IPayableSchema,
  IPayRequest,
  IPayrollSettings,
  IPayrollSettingsUpdate,
  IScheduleDateUpdate,
  MemberWorkFlowStatus,
  PayrollWorkflowStrategy,
  ScheduleStatus,
  WorkFlowSubStatus
} from "@wingspanhq/payments/dist/interfaces";
import {
  WSMutationConfig,
  WSMutationsConfig,
  WSQueryCache
} from "@ws-react-query";
import { isEqual } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { InvoicesFormValues } from "../../Invoices/components/InvoicesForm/InvoicesForm";
import {
  PayableFormValues,
  SendPayableInvoiceAction
} from "../../Invoices/components/PayableForm/PayableForm";
import { useDownloadPdf } from "../../Invoices/utils";
import {
  attachmentsFormDataToCreateRequest,
  attachmentsFormDataToUpdateRequest,
  convertToLineItemsCreateRequest,
  convertToLineItemsUpdateRequest,
  convertToLineItemsUpdateRequest2,
  convertToLineItemsV3CreateRequest,
  creditFeeFormDataToRequest,
  generateDefaultDueDateSetByPayer,
  generateDueDate,
  generateFrequencyConfig,
  generateLateFeeConfig,
  generateSendDate,
  paymentMethodsFormDataToCreateRequest,
  paymentMethodsFormDataToUpdateRequest,
  useInvoiceFormGoBack
} from "../../Invoices/utils/invoicesFormUtils";
import { paymentsService } from "../../services/payments";
import { convertDateToMidday, isToday } from "../../utils/dates";
import { getChangedData } from "../../utils/getChangedData";
import { useWSMutation } from "../helpers";
import { useUserId } from "../hooks/helpers";
import {
  QUERY_CLIENT_INVOICE,
  QUERY_CLIENT_INVOICE_TEMPLATE,
  QUERY_COLLABORATOR_DEDUCTIONS,
  QUERY_INVOICE,
  QUERY_INVOICE_TEMPLATE,
  QUERY_INVOICE_TEMPLATES,
  QUERY_INVOICES,
  QUERY_PAYABLE,
  QUERY_PAYABLES,
  QUERY_PAYROLL_SETTINGS
} from "./keys";

import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import { IDeductionCreateRequest } from "@wingspanhq/payments/dist/interfaces/api/deductions";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { transformErrorMessage } from "../../components/WSErrorMessage/WSErrorMessage";
import {
  CreditFeeConfig,
  InvoiceSplitCreate,
  LateFeeConfig,
  LateFeeConfigFrequencyInterval,
  PaymentDocumentCurrency,
  PaymentMethod as PaymentMethodV3
} from "@wingspanhq/wingspan-api-sdk";
import { invoicesApi, payersApi } from "@api";
import { queriesInvoices, queriesInvoiceRows } from "@query";
import { ErrorContextKey } from "../../services/platform";
import { useDefaultCurrency } from "../../shared/hooks/useDefaultCurrency";
import { WSServiceError } from "../../utils/serviceHelper";
import { QUERY_PAYER_ROWS } from "../search/payer/queries/useQueryPayerRows";

const findCreatedInvoice = async (
  invoiceTemplateId: string
): Promise<IInvoice | undefined> => {
  try {
    return (await paymentsService.invoice.list()).find(
      invoice => invoice.invoiceTemplateId === invoiceTemplateId
    );
  } catch (error) {
    console.error("Failed to fetch invoices: ", error);
  }
};

export const useMutationSendInvoiceTemplate = () => {
  const history = useHistory();

  return useWSMutation<
    any,
    WSServiceError,
    {
      invoiceTemplateId: string;
    }
  >(
    async ({ invoiceTemplateId }) => {
      await paymentsService.invoiceTemplate.update(invoiceTemplateId, {
        status: FrequencyAndScheduleStatus.Active
      });

      const createdInvoice = await findCreatedInvoice(invoiceTemplateId);

      if (createdInvoice) {
        history.push(
          `/member/invoices/${createdInvoice.invoiceId}/send-success`
        );
      } else {
        history.push(
          `/member/invoices/template/${invoiceTemplateId}/send-success`
        );
      }
    },
    {
      throwOnError: true,
      dependencies: [QUERY_INVOICE_TEMPLATES]
    }
  );
};

export const useMutationSendNowInvoiceTemplate = () => {
  const history = useHistory();

  return useWSMutation<
    any,
    WSServiceError,
    {
      invoiceTemplateId: string;
    }
  >(
    async ({ invoiceTemplateId }) => {
      await paymentsService.invoiceTemplate.update(invoiceTemplateId, {
        scheduleDates: [{ date: new Date(), status: ScheduleStatus.Pending }],
        status: FrequencyAndScheduleStatus.Active
      });

      const createdInvoice = await findCreatedInvoice(invoiceTemplateId);

      if (createdInvoice) {
        history.push(
          `/member/invoices/${createdInvoice.invoiceId}/send-success`
        );
      } else {
        history.push(
          `/member/invoices/template/${invoiceTemplateId}/send-success`
        );
      }
    },
    {
      throwOnError: true,
      dependencies: [QUERY_INVOICE_TEMPLATES]
    }
  );
};

export const useMutationAcceptInvoice = () => {
  const { openSnackbar } = useWSSnackbar();

  return useWSMutation<
    any,
    WSServiceError,
    {
      invoiceId: string;
    }
  >(
    async ({ invoiceId }) => {
      await paymentsService.invoice.update(invoiceId, {
        member: {
          workflowStatus: MemberWorkFlowStatus.Accepted
        }
      });
    },
    {
      onSuccess: (_, { invoiceId }) => {
        openSnackbar({
          type: "success",
          message: "Invoice accepted"
        });

        WSQueryCache.invalidateQueries(QUERY_INVOICES);
        WSQueryCache.invalidateQueries(
          queriesInvoiceRows.searchInvoiceRows.queryKeyBase
        );
        WSQueryCache.invalidateQueries([QUERY_INVOICE, invoiceId]);
      }
    }
  );
};

export const useMutationRetryInvoicePayment = () => {
  const { openSnackbar } = useWSSnackbar();

  return useWSMutation<
    any,
    WSServiceError,
    {
      invoiceId: string;
    }
  >(
    async ({ invoiceId }) => {
      await paymentsService.invoice.pay(invoiceId);
    },
    {
      onSuccess: (_, { invoiceId }) => {
        WSQueryCache.invalidateQueries(QUERY_INVOICES);
        WSQueryCache.invalidateQueries(
          queriesInvoiceRows.searchInvoiceRows.queryKeyBase
        );
        WSQueryCache.invalidateQueries([QUERY_INVOICE, invoiceId]);
      },
      onError: () => {
        openSnackbar({ type: "warning", message: "Failed to retry payment" });
      }
    }
  );
};

export const useMutationCancelInvoiceTemplate = () => {
  return useWSMutation<
    IInvoiceTemplate,
    WSServiceError,
    {
      invoiceTemplateId: string;
    }
  >(
    async ({ invoiceTemplateId }) => {
      return await paymentsService.invoiceTemplate.update(invoiceTemplateId, {
        status: FrequencyAndScheduleStatus.Expired,
        frequency: {
          endDate: new Date()
        }
      });
    },
    {
      throwOnError: true,
      dependencies: [QUERY_INVOICE_TEMPLATES]
    }
  );
};

export const useMutationDeleteInvoiceTemplate = () => {
  return useWSMutation<
    IInvoiceTemplate,
    WSServiceError,
    {
      invoiceTemplateId: string;
    }
  >(
    async ({ invoiceTemplateId }) => {
      return await paymentsService.invoiceTemplate.delete(invoiceTemplateId);
    },
    {
      throwOnError: true,
      dependencies: [QUERY_INVOICE_TEMPLATES]
    }
  );
};

export const useMutationSaveInvoice = () => {
  const userId = useUserId();
  const goBack = useInvoiceFormGoBack();
  const history = useHistory();
  const location = useLocation();

  return useWSMutation<
    {
      savedInvoice?: IInvoice;
      savedInvoiceId?: string;
      savedInvoiceTemplate?: IInvoiceTemplate;
    },
    WSServiceError,
    {
      invoiceId?: string;
      invoiceTemplateId?: string;
      data: InvoicesFormValues & {
        dueInDays?: number;
      };
      send?: boolean;
      resubmit?: boolean;
      currency: Currency;
    }
  >(
    async ({
      invoiceId,
      invoiceTemplateId,
      data,
      send,
      resubmit,
      currency
    }) => {
      let invoice: IInvoice | undefined;
      let invoiceTemplate: IInvoiceTemplate | undefined;
      let savedInvoice: IInvoice | undefined;
      let savedInvoiceId: string | undefined;
      let savedInvoiceTemplate: IInvoiceTemplate | undefined;

      if (invoiceId) {
        invoice = await paymentsService.invoice.get(invoiceId);
      }

      if (invoiceTemplateId) {
        invoiceTemplate =
          await paymentsService.invoiceTemplate.get(invoiceTemplateId);
      }

      // Client
      let memberClientId: string | undefined;

      // DOCUMENTED GAP (v3): `data.client.payerId` comes from the v1 `/payer-row` search
      // (ClientSection → useQueryPayerRows), so it is a **v1** payer-relationship id. v1 and
      // v3 payer ids are DISJOINT (unlike payee ids — the notifications service had to be
      // changed to emit v3 payer ids for the W9-consent flow, proving v1 payer ids don't
      // resolve on v3). The CC read/write below is migrated to v3
      // (`Payer.notificationCcEmails` / `PayerUpdate.notificationCcEmails`, WIN-15229), but
      // `getPayer` will 404 on this v1 id until the invoice client picker is migrated to v3,
      // so the CC sync is skipped (caught below) in the interim — invoice-form CC edits won't
      // persist here, though CC stays fully editable on v3 via the client-edit modal.
      // See V3_MIGRATION_TRACKER.md (payers, invoice-save CC).
      const payerId = data.client?.payerId as string;
      try {
        const payerPayeeEngagementId = data.client
          ?.payerPayeeEngagementId as string;
        memberClientId = payerPayeeEngagementId;

        const payer = await payersApi.getPayer({ payerId });

        const additionalEmails = (data.client?.emailsCC || [])
          .map(i => i.email)
          .filter(e => !!e);
        if (!isEqual(additionalEmails, payer.notificationCcEmails || [])) {
          // `[]` explicitly clears the stored list (WIN-15229 semantics); omitting the
          // field would preserve it.
          await payersApi.updatePayer({
            payerId,
            payerUpdate: { notificationCcEmails: additionalEmails }
          });
        }
      } catch (error) {
        console.error("Failed to update client email CCs", error);
      }

      if (!memberClientId) {
        throw new Error("No memberClientId set");
      }

      let collaboratorsRequest: (IInvoiceCollaboratorCreateRequest | null)[];

      collaboratorsRequest = data.invoiceCollaborators.map(
        invoiceCollaborator => {
          if (
            invoiceCollaborator.remove ||
            !invoiceCollaborator.payee?.payerPayeeEngagementId ||
            !invoiceCollaborator.amount
          )
            return null;

          return {
            memberClientId: invoiceCollaborator.payee.payerPayeeEngagementId,
            amount: invoiceCollaborator.amount,
            currency,
            description: invoiceCollaborator.description
          };
        }
      );

      const collaboratorsCreateRequestData = collaboratorsRequest.filter(
        collaborator => !!collaborator
      ) as IInvoiceCollaboratorCreateRequest[];

      const sendDate = generateSendDate({
        send: data.send
      });

      let dueDate: Date;
      if (data.dueInDays !== undefined) {
        // if dueInDays is set by payer then generate due date based on it
        dueDate = generateDefaultDueDateSetByPayer({
          dueInDays: data.dueInDays,
          sendDate
        });
      } else {
        // Generate due date based on form values and send date
        dueDate = generateDueDate({
          formValues: {
            due: data.due,
            customDueDate: data.customDueDate
          },
          sendDate
        });
      }

      // Generate object with common invoice data which can be used for both invoice and invoiceTemplate
      const commonData = {
        memberId: userId,
        currency,
        memberClientId,
        clientId: payerId,
        invoiceNotes: data.other.notes,
        creditFeeHandling: creditFeeFormDataToRequest(
          data.advanced.creditFeeHandling
        ),
        notificationPreferences: {
          sendInvoice: data.advanced.sendEmails,
          sendReminders:
            data.advanced.sendEmails && !!data.advanced.sendReminders
        },
        dueDate,
        lateFeeHandling: data.includesLateFee
          ? generateLateFeeConfig({
              formValues: data.lateFee,
              dueDate
            })
          : undefined,
        attachments: {
          customAttachmentIds: attachmentsFormDataToCreateRequest(
            data.other.attachments
          )
        },
        acceptedPaymentMethods: paymentMethodsFormDataToCreateRequest(
          data.paymentMethods
        ),
        labels: {
          projectName: data.other.projectName || (undefined as any as string),
          subject: data.subject
        },
        metadata: {
          invoiceDate: convertDateToMidday(data.advanced.date),
          purchaseOrderNumber: data.purchaseOrderNumber || undefined
        }
      };

      if (
        !data.recurring &&
        (data.send.type === "immediately" || isToday(sendDate))
      ) {
        // If invoice is not repeats and have to be sent immediately – we can work just with invoice entity

        if (invoiceId) {
          savedInvoice = await paymentsService.invoice.update(
            invoiceId,
            {
              ...getChangedData(
                invoice,
                {
                  ...commonData,
                  collaborators: collaboratorsRequest,
                  lineItems: convertToLineItemsUpdateRequest(data.lineItems),
                  lateFeeHandling:
                    invoice?.lateFeeHandling && !commonData.lateFeeHandling
                      ? {
                          lateFeePercentage: null,
                          lateFeeAmount: null,
                          frequency: null
                        }
                      : commonData.lateFeeHandling,
                  acceptedPaymentMethods: paymentMethodsFormDataToUpdateRequest(
                    data.paymentMethods
                  ),
                  attachments: {
                    customAttachmentIds: attachmentsFormDataToUpdateRequest(
                      data.other.attachments,
                      invoice?.attachments?.customAttachmentIds || []
                    )
                  },
                  member: resubmit
                    ? {
                        workflowSubStatus: WorkFlowSubStatus.Submitted
                      }
                    : undefined
                },
                { dismissTimeInDates: true }
              )
            },
            {
              waitForRowUpdate: true
            }
          );
          savedInvoiceId = savedInvoice.invoiceId;
        } else if (invoiceTemplateId) {
          // This is the case when member have one-time scheduled for future invoice but wants to send it now, so they updates send date to today in Edit form. In this case we're just updating existing invoiceTemplate and it will create new Open invoice.

          savedInvoiceTemplate = await paymentsService.invoiceTemplate.update(
            invoiceTemplateId,
            getChangedData(
              invoiceTemplate,
              {
                invoiceData: {
                  ...commonData,
                  collaborators: collaboratorsRequest,
                  lineItems: convertToLineItemsUpdateRequest(data.lineItems),
                  acceptedPaymentMethods: paymentMethodsFormDataToUpdateRequest(
                    data.paymentMethods
                  ),
                  attachments: {
                    customAttachmentIds: attachmentsFormDataToUpdateRequest(
                      data.other.attachments,
                      invoiceTemplate?.invoiceData.attachments
                        ?.customAttachmentIds || []
                    )
                  }
                },
                scheduleDates: [
                  {
                    date: sendDate
                  }
                ],
                labels: commonData.labels
              },
              { dismissTimeInDates: true }
            )
          );
        } else {
          const splits: InvoiceSplitCreate[] =
            data.invoiceCollaborators.flatMap(invoiceCollaborator => {
              if (
                invoiceCollaborator.remove ||
                !invoiceCollaborator.payee?.payeeId ||
                !invoiceCollaborator.payee?.payerPayeeEngagementId ||
                !invoiceCollaborator.amount
              ) {
                return [];
              }

              return [
                {
                  payeeId: invoiceCollaborator.payee.payeeId,
                  payeeEngagementId:
                    invoiceCollaborator.payee.payerPayeeEngagementId,
                  amount: invoiceCollaborator.amount,
                  currency,
                  description: invoiceCollaborator.description
                }
              ];
            });

          const acceptedPaymentMethods: PaymentMethodV3[] = data.paymentMethods
            .acceptAllPMs
            ? [
                PaymentMethodV3.CREDIT,
                PaymentMethodV3.ACH,
                PaymentMethodV3.MANUAL
              ]
            : [
                ...(data.paymentMethods.acceptCreditCard
                  ? [PaymentMethodV3.CREDIT]
                  : []),
                ...(data.paymentMethods.acceptACH ? [PaymentMethodV3.ACH] : []),
                ...(data.paymentMethods.acceptManual
                  ? [PaymentMethodV3.MANUAL]
                  : [])
              ];

          const creditFeeHandling: CreditFeeConfig = data.advanced
            .creditFeeHandling?.clientPaysProcessingFees
            ? {
                payerFlatFeePercentage:
                  data.advanced.creditFeeHandling.clientProcessingFeePercentage
              }
            : { payeeFeePercentage: 100 };

          let lateFeeHandling: LateFeeConfig | undefined;
          if (data.includesLateFee) {
            lateFeeHandling = data.lateFee.overriden
              ? {
                  lateFeeAmount: data.lateFee.compoundedAmount,
                  frequency: {
                    interval:
                      LateFeeConfigFrequencyInterval.LATE_FEE_INTERVAL_WEEKLY,
                    every: 1
                  }
                }
              : {
                  ...(data.lateFee.type === "percent"
                    ? { lateFeePercentage: data.lateFee.amount }
                    : {}),
                  ...(data.lateFee.type === "fixed"
                    ? { lateFeeAmount: data.lateFee.amount }
                    : {}),
                  frequency: {
                    interval:
                      data.lateFee.interval === Intervals.Month
                        ? LateFeeConfigFrequencyInterval.LATE_FEE_INTERVAL_MONTHLY
                        : LateFeeConfigFrequencyInterval.LATE_FEE_INTERVAL_WEEKLY,
                    every: data.lateFee.every
                  }
                };
          }

          const createdInvoice = await invoicesApi.createInvoice({
            invoiceCreate: {
              payerId,
              payerEngagementId: memberClientId,
              currency: currency as unknown as PaymentDocumentCurrency,
              dueDate,
              lineItems: convertToLineItemsV3CreateRequest(data.lineItems),
              splits,
              notes: data.other.notes,
              acceptedPaymentMethods,
              creditFeeHandling,
              lateFeeHandling,
              notificationPreferences: {
                shouldSendInvoice: data.advanced.sendEmails,
                shouldSendReminders:
                  data.advanced.sendEmails && !!data.advanced.sendReminders
              },
              metadata: {
                subject: data.subject,
                ...(data.other.projectName
                  ? { projectName: data.other.projectName }
                  : {}),
                invoiceDate: convertDateToMidday(
                  data.advanced.date
                ).toISOString(),
                ...(data.purchaseOrderNumber
                  ? { purchaseOrderNumber: data.purchaseOrderNumber }
                  : {})
              }
            }
          });
          savedInvoiceId = createdInvoice.id;
        }
      } else {
        // If invoice repeats OR sending should be delayed – we are working with invoiceTemplate entity

        // Generate frequency config if invoice have to be repeated
        const frequency = data.recurring
          ? generateFrequencyConfig({
              formValues: data.frequency,
              sendDate
            })
          : undefined;

        if (invoiceTemplateId) {
          let scheduleDates: (IScheduleDateUpdate | null)[] | undefined;

          if (invoiceTemplate?.isSchedulingOnly) {
            // New scheduling logic

            if (frequency) {
              // If invoice is repeatable

              if (frequency.endDate) {
                // If repeatable invoice has end date – we need to cut off everything after end date

                scheduleDates = invoiceTemplate?.scheduleDates?.map(
                  scheduleDate =>
                    frequency.endDate && scheduleDate.date > frequency.endDate
                      ? null
                      : {}
                );
              }

              // If repeatable invoice is endless don't do anything
            } else {
              // If it's one time scheduled invoice, just update send date

              scheduleDates = [
                {
                  date: sendDate
                }
              ];
            }
          } else {
            // Old scheduling logic

            scheduleDates = [
              {
                date: sendDate
              }
            ];
          }

          // If invoiceTemplate is being edited – update invoiceTemplate
          savedInvoiceTemplate = await paymentsService.invoiceTemplate.update(
            invoiceTemplateId,
            getChangedData(
              invoiceTemplate,
              {
                invoiceData: {
                  ...commonData,
                  collaborators: collaboratorsRequest,
                  lineItems: convertToLineItemsUpdateRequest2(
                    invoiceTemplate?.invoiceData.lineItems || [],
                    data.lineItems
                  ),
                  lateFeeHandling:
                    invoiceTemplate?.invoiceData.lateFeeHandling &&
                    !commonData.lateFeeHandling
                      ? {
                          lateFeePercentage: null,
                          lateFeeAmount: null,
                          frequency: null
                        }
                      : commonData.lateFeeHandling,
                  acceptedPaymentMethods: paymentMethodsFormDataToUpdateRequest(
                    data.paymentMethods
                  ),
                  attachments: {
                    customAttachmentIds: attachmentsFormDataToUpdateRequest(
                      data.other.attachments,
                      invoiceTemplate?.invoiceData.attachments
                        ?.customAttachmentIds || []
                    )
                  }
                },
                frequency: frequency
                  ? frequency
                  : invoiceTemplate?.frequency
                    ? null
                    : undefined,
                scheduleDates,
                labels: commonData.labels
              },
              { dismissTimeInDates: true }
            )
          );
        } else {
          if (invoiceId) {
            if (invoice?.status === InvoiceStatus.Draft) {
              await paymentsService.invoice.delete(invoiceId);
            }
          }

          // Create new invoiceTemplate
          savedInvoiceTemplate = await paymentsService.invoiceTemplate.create({
            invoiceData: {
              ...commonData,
              collaborators: collaboratorsCreateRequestData,
              lineItems: convertToLineItemsCreateRequest(data.lineItems)
            },
            frequency,
            scheduleDates: [{ date: sendDate, status: ScheduleStatus.Pending }],
            status: FrequencyAndScheduleStatus.Draft,
            labels: commonData.labels,
            isSchedulingOnly: true
          });
        }
      }

      if (send) {
        if (savedInvoiceId) {
          await invoicesApi.sendInvoice({ invoiceId: savedInvoiceId });

          history.push(
            `/member/invoices/${savedInvoiceId}/send-success`,
            location.state
          );
        }

        if (savedInvoiceTemplate) {
          savedInvoiceTemplate = await paymentsService.invoiceTemplate.update(
            savedInvoiceTemplate.invoiceTemplateId,
            {
              status: FrequencyAndScheduleStatus.Active
            }
          );

          const createdInvoice = await findCreatedInvoice(
            savedInvoiceTemplate.invoiceTemplateId
          );

          if (createdInvoice) {
            history.push(
              `/member/invoices/${createdInvoice.invoiceId}/send-success`,
              location.state
            );
          } else {
            history.push(
              `/member/invoices/template/${savedInvoiceTemplate.invoiceTemplateId}/send-success`,
              location.state
            );
          }
        }
      }

      return {
        savedInvoice,
        savedInvoiceId,
        savedInvoiceTemplate
      };
    },
    {
      throwOnError: false,
      onSuccess: ({ savedInvoiceId, savedInvoiceTemplate }, { send }) => {
        WSQueryCache.invalidateQueries(QUERY_INVOICES);
        WSQueryCache.invalidateQueries(QUERY_INVOICE_TEMPLATES);
        WSQueryCache.invalidateQueries(
          queriesInvoiceRows.searchInvoiceRows.queryKeyBase
        );
        WSQueryCache.invalidateQueries(QUERY_PAYER_ROWS);
        WSQueryCache.invalidateQueries(queriesInvoices.getInvoice.queryKeyBase);
        WSQueryCache.invalidateQueries(
          queriesInvoices.listInvoices.queryKeyBase
        );
        WSQueryCache.invalidateQueries(
          queriesInvoices.searchInvoices.queryKeyBase
        );

        if (savedInvoiceId) {
          WSQueryCache.invalidateQueries([QUERY_INVOICE, savedInvoiceId]);
        }
        if (savedInvoiceTemplate) {
          WSQueryCache.invalidateQueries([
            QUERY_INVOICE_TEMPLATE,
            savedInvoiceTemplate.invoiceTemplateId
          ]);
        }

        if (!send) {
          goBack();
        }
      }
    }
  );
};

export const useMutationSendInvoice = () => {
  const history = useHistory();
  const location = useLocation();
  const { openSnackbar } = useWSSnackbar();

  return useWSMutation<
    {
      invoiceTemplate?: IInvoiceTemplate;
    },
    WSServiceError,
    {
      invoiceId?: string;
      invoiceTemplateId?: string;
    }
  >(
    async ({ invoiceId, invoiceTemplateId }) => {
      let invoiceTemplate: IInvoiceTemplate | undefined;

      if (invoiceId) {
        // Sending a single invoice is a clean v3 op (draft → sent); the
        // recurring-template activation below stays on v1.
        await invoicesApi.sendInvoice({ invoiceId });

        WSQueryCache.invalidateQueries([QUERY_INVOICE, invoiceId]);
        WSQueryCache.invalidateQueries(
          queriesInvoices.getInvoice.getQueryKey({ invoiceId })
        );

        history.push(
          `/member/invoices/${invoiceId}/send-success`,
          location.state
        );
      }

      if (invoiceTemplateId) {
        invoiceTemplate = await paymentsService.invoiceTemplate.update(
          invoiceTemplateId,
          {
            status: FrequencyAndScheduleStatus.Active
          }
        );

        const createdInvoice = await findCreatedInvoice(invoiceTemplateId);

        if (createdInvoice) {
          history.push(
            `/member/invoices/${createdInvoice.invoiceId}/send-success`,
            location.state
          );
        } else {
          history.push(
            `/member/invoices/template/${invoiceTemplateId}/send-success`,
            location.state
          );
        }
      }

      return {
        invoiceTemplate
      };
    },
    {
      throwOnError: false,
      onSuccess: ({ invoiceTemplate }) => {
        openSnackbar({
          type: "success",
          message: "Invoice has been sent"
        });

        WSQueryCache.invalidateQueries(QUERY_INVOICES);
        WSQueryCache.invalidateQueries(
          queriesInvoiceRows.searchInvoiceRows.queryKeyBase
        );

        if (invoiceTemplate) {
          WSQueryCache.invalidateQueries([
            QUERY_INVOICE_TEMPLATE,
            invoiceTemplate.invoiceTemplateId
          ]);
          WSQueryCache.invalidateQueries(QUERY_INVOICE_TEMPLATES);
        }
      },
      onError: error => {
        openSnackbar({
          type: "warning",
          message: transformErrorMessage({
            error,
            contextKey: ErrorContextKey.InvoiceForm
          })
        });
      }
    }
  );
};

export const useMutationCreateClientInvoice = () => {
  return useWSMutation<
    IClientInvoice,
    WSServiceError,
    IClientInvoiceCreateRequest
  >(data => paymentsService.client.invoice.create(data), {
    throwOnError: false,
    dependencies: [QUERY_CLIENT_INVOICE]
  });
};

export const useMutationUpdateClientInvoice = (invoiceId: string) => {
  return useWSMutation<
    IClientInvoice,
    WSServiceError,
    IClientInvoiceUpdateRequest
  >(data => paymentsService.client.invoice.update(invoiceId, data), {
    throwOnError: false,
    dependencies: [[QUERY_CLIENT_INVOICE, invoiceId]]
  });
};

export const useMutationPayClientInvoice = (
  invoiceId: string,
  config?: WSMutationsConfig<IClientInvoice, WSServiceError, IPayRequest>
) => {
  return useWSMutation<IClientInvoice, WSServiceError, IPayRequest>(
    data => paymentsService.client.invoice.pay(invoiceId, data),
    {
      throwOnError: false,
      dependencies: [[QUERY_CLIENT_INVOICE, invoiceId]],
      ...config
    }
  );
};

export const useMutationUpdateClientInvoiceTemplate = () => {
  return useWSMutation<
    IClientInvoiceTemplate,
    WSServiceError,
    { invoiceTemplateId: string; data: IClientInvoiceTemplateUpdateRequest }
  >(
    ({ invoiceTemplateId, data }) =>
      paymentsService.client.invoiceTemplate.update(invoiceTemplateId, data),
    {
      throwOnError: false,
      dependencies: [QUERY_CLIENT_INVOICE_TEMPLATE]
    }
  );
};

export const useMutationSavePayable = () => {
  const defaultCurrency = useDefaultCurrency();

  return useWSMutation<
    {
      savedPayable: IPayableSchema;
      savedCollaborator?: ICollaboratorSchema;
    },
    WSServiceError,
    {
      payableId?: string;
      data: PayableFormValues;
      resubmit?: boolean;
    }
  >(
    async ({ data, payableId, resubmit }) => {
      let savedCollaborator: ICollaboratorSchema | undefined;
      let savedPayable: IPayableSchema;

      const commonData = {
        currency: defaultCurrency,
        invoiceNotes: data.other.notes,
        labels: data.other.projectName
          ? {
              projectName: data.other.projectName
            }
          : undefined,
        attachments: {
          customAttachmentIds: attachmentsFormDataToCreateRequest(
            data.other.attachments
          )
        },
        client: {
          payDate: data.payDate
        },
        creditFeeHandling: {
          memberPays: 0,
          clientPays: 100
        },
        metadata: {
          purchaseOrderNumber: data.purchaseOrderNumber || undefined
        }
      };

      if (payableId) {
        const payable = await paymentsService.payable.get(payableId);

        savedPayable = await paymentsService.payable.update(
          payableId,
          getChangedData(
            payable,
            {
              ...commonData,
              lineItems:
                data.lineItems && data.lineItems.length > 0
                  ? convertToLineItemsUpdateRequest(data.lineItems)
                  : [
                      {
                        description: "",
                        totalCost: 0
                      }
                    ],
              attachments: {
                customAttachmentIds: attachmentsFormDataToUpdateRequest(
                  data.other.attachments,
                  payable.attachments?.customAttachmentIds || []
                )
              },
              client: {
                ...commonData.client,
                workflowSubStatus: resubmit
                  ? WorkFlowSubStatus.Submitted
                  : undefined
              }
            },
            { dismissTimeInDates: true }
          )
        );
      } else {
        if (!data.payee) throw new Error("No payee set");

        const collaboratorId = data.payee?.payerPayeeEngagementId;

        savedPayable = await paymentsService.payable.create({
          ...commonData,
          collaboratorId,
          lineItems:
            data.lineItems && data.lineItems.length > 0
              ? convertToLineItemsCreateRequest(data.lineItems)
              : [
                  {
                    description: "",
                    totalCost: 0
                  }
                ],
          dueDate: data.payDate
        });
      }

      return {
        savedCollaborator,
        savedPayable
      };
    },
    {
      throwOnError: false,
      dependencies: [QUERY_PAYABLES]
    }
  );
};

export const useMutationSendPayable = () => {
  return useWSMutation<
    {
      payable?: IPayableSchema;
    },
    WSServiceError,
    {
      action: SendPayableInvoiceAction;
      payableId: string;
    }
  >(
    async ({ action, payableId }) => {
      let payable: IPayableSchema;

      if (action === "approve") {
        payable = await paymentsService.payable.update(payableId, {
          status: InvoiceStatus.Open,
          client: {
            workflowStatus: ClientWorkFlowStatus.Approved
          }
        });
      } else {
        payable = await paymentsService.payable.update(payableId, {
          status: InvoiceStatus.Open
        });
      }

      return {
        payable
      };
    },
    {
      throwOnError: false,
      onSuccess: ({ payable }) => {
        WSQueryCache.invalidateQueries(QUERY_PAYABLES);
        if (payable) {
          WSQueryCache.invalidateQueries([QUERY_PAYABLE, payable?.payableId]);
        }
      }
    }
  );
};

export const useMutationUpdatePayrollSettings = (
  memberId: string,
  config?: WSMutationConfig<IPayrollSettings, WSServiceError>
) => {
  return useWSMutation<
    IPayrollSettings,
    WSServiceError,
    IPayrollSettingsUpdate
  >(
    async data => {
      await paymentsService.service.get();
      return paymentsService.payrollSettings.update(memberId, data);
    },
    {
      dependencies: [QUERY_PAYROLL_SETTINGS],
      ...config
    }
  );
};

export const useMutationDownloadInvoicePdf = () => {
  const downloadPdf = useDownloadPdf();

  return useWSMutation<
    void,
    WSServiceError,
    {
      invoiceId: string;
    }
  >(async ({ invoiceId }) => {
    const invoice = await paymentsService.invoice.generatePdf(invoiceId);

    if (!invoice.attachments?.invoicePdf) {
      throw new Error("No invoice pdf to download");
    }

    downloadPdf(invoice.attachments.invoicePdf);
  });
};

export const useMutationDownloadInvoiceReceiptPdf = () => {
  const downloadPdf = useDownloadPdf();

  return useWSMutation<
    void,
    WSServiceError,
    {
      invoiceId: string;
    }
  >(async ({ invoiceId }) => {
    const invoice = await paymentsService.invoice.generatePdf(invoiceId);

    if (!invoice.attachments?.receiptPdf) {
      throw new Error("No invoice receipt to download");
    }

    downloadPdf(invoice.attachments.receiptPdf);
  });
};

export const useMutationCreateCollaboratorDeduction = () =>
  useWSMutation(
    (data: IDeductionCreateRequest) =>
      paymentsService.collaboratorDeductions.create(data),
    {
      dependencies: [QUERY_COLLABORATOR_DEDUCTIONS]
    }
  );

export const useMutationDeleteCollaboratorDeduction = (
  config?: WSMutationConfig<IDeductionResponse, WSServiceError>
) =>
  useWSMutation<IDeductionResponse, WSServiceError>(
    (id: string) => paymentsService.collaboratorDeductions.delete(id),
    {
      dependencies: [QUERY_COLLABORATOR_DEDUCTIONS],
      ...config
    }
  );
